/* Building blocks shared by all printable sheets (letterhead, running head, signatures,
 * verification QR, barcode, document title). Ported from print.js. */
import type { ReactNode } from "react";
import type { Employee, Tenant } from "../../../data/types";
import { gDate as gDateL, hash } from "../../../lib/format";
import type { Dims } from "../papers";
import type { HeadKind, PrState } from "../state";
import { activeCountry } from "../../../lib/country";

/** Everything a sheet builder needs. */
export interface PCtx {
  s: PrState;
  d: Dims;
  tenant: Tenant;
  today: string;
  nowHm: string;
  printedBy: string;
  /** bilingual label per the chosen print language */
  lb: (en: string, ar: string) => ReactNode;
  /** single-language label (Arabic only when language = Arabic) */
  lb1: (en: string, ar: string) => string;
  gd: (iso: string) => string;
  dept: (k: string) => string;
  branch: (k: string) => string;
  nat: (k: string) => string;
  title: (e: Employee) => string;
}

export function makeLb(lang: PrState["lang"]) {
  const lb = (en: string, ar: string): ReactNode => (lang === "en" ? en : lang === "ar" ? ar : <>{en} <span className="ar">{ar}</span></>);
  const lb1 = (en: string, ar: string) => (lang === "ar" ? ar : en);
  const gd = (iso: string) => gDateL(iso, lang === "ar" ? "ar" : "en");
  return { lb, lb1, gd };
}

export const n2 = (v: number) => (Number.isFinite(v) ? v : 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/** Deterministic QR-like verification code (finder patterns + hashed modules) — as in the prototype. */
export function Qr({ seed, size }: { seed: string; size: number }) {
  const N = 21, c = size / N;
  let h0 = hash(String(seed));
  const cells: ReactNode[] = [];
  for (let y = 0; y < N; y++) for (let x = 0; x < N; x++) {
    if ((x < 8 && y < 8) || (x > 12 && y < 8) || (x < 8 && y > 12)) continue;
    h0 = (Math.imul(h0, 1103515245) + 12345) >>> 0;
    if ((h0 >>> 16) % 2) cells.push(<rect key={x + "-" + y} x={x * c} y={y * c} width={c} height={c} fill="#000" />);
  }
  const fin = (x: number, y: number) => (
    <g key={"f" + x + y}>
      <rect x={x * c} y={y * c} width={7 * c} height={7 * c} fill="#000" />
      <rect x={(x + 1) * c} y={(y + 1) * c} width={5 * c} height={5 * c} fill="#fff" />
      <rect x={(x + 2) * c} y={(y + 2) * c} width={3 * c} height={3 * c} fill="#000" />
    </g>
  );
  return (
    <svg className="qr" viewBox={`0 0 ${size} ${size}`} width={size + "mm"} height={size + "mm"} role="img" aria-label="Verification code">
      <rect width={size} height={size} fill="#fff" />{cells}{fin(0, 0)}{fin(14, 0)}{fin(0, 14)}
    </svg>
  );
}

export function Barcode({ text, w, h }: { text: string; w: number; h: number }) {
  const bars: ReactNode[] = [];
  let x = 0, h0 = hash(text);
  while (x < 200) {
    h0 = (Math.imul(h0, 1664525) + 1013904223) >>> 0;
    const bw = 1 + ((h0 >>> 28) % 3);
    if ((h0 >>> 20) % 3) bars.push(<rect key={x} x={x} y={0} width={bw} height={40} fill="#000" />);
    x += bw + 1 + ((h0 >>> 24) % 2);
  }
  return <svg className="bc" viewBox="0 0 200 40" preserveAspectRatio="none" width={w + "mm"} height={h + "mm"} role="img" aria-label={"Barcode " + text}>{bars}</svg>;
}

export function Letterhead({ c, kind }: { c: PCtx; kind: HeadKind }) {
  const t = c.tenant;
  if (kind === "none") return null;
  if (kind === "pre") return <div className="lh-pre" title="Space kept for pre-printed letterhead" />;
  if (kind === "simple") return <div className="lh-simple"><b>{c.lb1(t.name, t.nameAr)}</b><span>CR {t.cr} · VAT {t.vat}</span></div>;
  return (
    <div className="lh">
      <div className="lh-l"><span className="lh-seal">{(t.name[0] || "R").toUpperCase()}</span><div><div className="lh-name">{t.name}</div><div className="lh-sub">{t.city} · CR {t.cr} · VAT {t.vat}</div></div></div>
      <div className="lh-r ar"><div className="lh-name">{t.nameAr}</div><div className="lh-sub">س.ت {t.cr} · الرقم الضريبي {t.vat}</div></div>
    </div>
  );
}

export function RunHead({ c, title, pg, tot }: { c: PCtx; title: string; pg: number; tot: number }) {
  return <div className="rh"><b>{c.lb1(c.tenant.name, c.tenant.nameAr)}</b><span>{title}</span><span className="num">{c.lb1("Page ", "صفحة ")}{pg} / {tot}</span></div>;
}

export function SignBlock({ c, roles }: { c: PCtx; roles: [string, string][] }) {
  if (!c.s.sign) return null;
  return (
    <div className="signs">
      {roles.map((r) => <div className="sign" key={r[0]}><div className="sign-line" /><div>{c.lb(r[0], r[1])}</div><div className="sign-sub">{c.lb("Name · signature · date", "الاسم · التوقيع · التاريخ")}</div></div>)}
      {c.s.stamp && <div className="sign stamp-box"><div className="stamp-c">{c.lb1("Company stamp", "ختم المنشأة")}</div></div>}
    </div>
  );
}

export function VerifyBlock({ c, refNo }: { c: PCtx; refNo: string }) {
  if (!c.s.qr) return null;
  return (
    <div className="verify">
      <Qr seed={refNo} size={18} />
      <div><b>{c.lb("Verify this document", "تحقق من الوثيقة")}</b><div className="num">verify.ruknhr.sa/{refNo}</div><div className="muted2">{c.lb("Scan to confirm it was issued by StartHR", "امسح الرمز للتأكد من صدورها من ستارت HR")}</div></div>
    </div>
  );
}

export function DocTitle({ c, en, ar, meta }: { c: PCtx; en: string; ar: string; meta?: ReactNode }) {
  return <div className="dt"><div><h1>{c.lb(en, ar)}</h1>{meta != null && <div className="dt-meta">{meta}</div>}</div></div>;
}

/** Employee name in the chosen print language. */
export const empName = (c: PCtx, e: Employee) => (c.s.lang === "ar" ? e.nameAr : c.s.lang === "en" ? e.nameEn : e.nameEn + " · " + e.nameAr);
export const maskIban = (iban: string, head = 6) => { const s = iban.replace(/\s/g, ""); return s.slice(0, head) + "••••" + s.slice(-4); };

/** Minor unit of each currency (100 halalas, fils or dirhams; 1,000 baisa or fils in Oman, Bahrain and Kuwait). */
const SUBUNIT: Record<string, string> = { SA: "halalas", AE: "fils", OM: "baisa", QA: "dirhams", BH: "fils", KW: "fils" };
export function amountWords(n: number): string {
  const ones = ["", "one", "two", "three", "four", "five", "six", "seven", "eight", "nine", "ten", "eleven", "twelve", "thirteen", "fourteen", "fifteen", "sixteen", "seventeen", "eighteen", "nineteen"];
  const tens = ["", "", "twenty", "thirty", "forty", "fifty", "sixty", "seventy", "eighty", "ninety"];
  const w = (x: number): string => (x < 20 ? ones[x] : x < 100 ? tens[Math.floor(x / 10)] + (x % 10 ? "-" + ones[x % 10] : "") : ones[Math.floor(x / 100)] + " hundred" + (x % 100 ? " " + w(x % 100) : ""));
  const whole = Math.floor(Math.max(0, n));
  const mil = Math.floor(whole / 1e6), th = Math.floor((whole % 1e6) / 1000), r = whole % 1000;
  const cur = activeCountry().currency;
  const sub = SUBUNIT[activeCountry().code] ?? "halalas";
  const halalas = Math.round((Math.max(0, n) - whole) * 10 ** cur.decimals);
  let s = [mil ? w(mil) + " million" : "", th ? w(th) + " thousand" : "", r ? w(r) : ""].filter(Boolean).join(" ");
  if (!s) s = "zero";
  return s.charAt(0).toUpperCase() + s.slice(1) + " " + cur.word[0] + "s" + (halalas ? " and " + w(halalas) + " " + sub : "") + " only";
}
